import { useState, useCallback } from "react";
import { Chat, Message } from "../../../types/conversations";

interface UseExportConversationParams {
  chat: Chat;
  messages: Message[];
  currentUserId?: number;
}

export const useExportConversation = ({
  chat,
  messages,
  currentUserId,
}: UseExportConversationParams) => {
  const [isExporting, setIsExporting] = useState(false);
  const [exportSuccess, setExportSuccess] = useState(false);

  const handleExportConversation = useCallback(async () => {
    if (!messages || messages.length === 0) {
      alert("Não há mensagens para exportar.");
      return;
    }

    setIsExporting(true);
    await new Promise((resolve) => setTimeout(resolve, 300));

    try {
      let exportText = `Conversa: ${chat.name}\n`;
      exportText += `Exportado em: ${new Date().toLocaleString("pt-BR")}\n`;
      exportText += `Total de mensagens: ${messages.length}\n`;
      exportText += `${"=".repeat(50)}\n\n`;

      messages.forEach((message) => {
        const date = new Date(message.timestamp);
        const isFromMe =
          currentUserId !== undefined && message.senderId === currentUserId;
        const sender = isFromMe ? "Você" : chat.name;

        exportText += `[${date.toLocaleString("pt-BR")}] ${sender}:\n`;

        if (message.type === "text") {
          exportText += `${message.content}\n\n`;
        } else if (message.type === "image") {
          exportText += `[Imagem: ${message.content}]\n`;
          if (message.fileUrl && message.fileUrl !== "#") {
            exportText += `URL: ${message.fileUrl}\n`;
          }
          exportText += "\n";
        } else if (message.type === "file") {
          exportText += `[Arquivo: ${message.fileName || message.content}]\n`;
          if (message.fileSize) {
            exportText += `Tamanho: ${message.fileSize}\n`;
          }
          if (message.fileUrl && message.fileUrl !== "#") {
            exportText += `URL: ${message.fileUrl}\n`;
          }
          exportText += "\n";
        }
      });

      const blob = new Blob([exportText], { type: "text/plain;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `conversa-${chat.name.replace(/\s+/g, "-")}-${
        new Date().toISOString().split("T")[0]
      }.txt`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setExportSuccess(true);
      setTimeout(() => setExportSuccess(false), 2000);
    } catch (error) {
      console.error("Erro ao exportar conversa:", error);
      alert("Erro ao exportar conversa. Tente novamente.");
    } finally {
      setIsExporting(false);
    }
  }, [chat, messages, currentUserId]);

  return { handleExportConversation, isExporting, exportSuccess };
};
